import VirtualGallery from '../VirtualGallery';
import useLatestRead from '../../hooks/useLatestRead';
import React, { useState, useEffect, useRef } from 'react';
import { buildAssetUrl, downscalerAPI } from '../../services/api';
import OptimizedImage, { getPreviewImagePath } from '../OptimizedImage';
import './Downscaler.css';

// Aspect ratio options (excluding 16:9)
const aspectRatioOptions = [
  { value: '4:5', label: '4:5 Post' },
  { value: '9:16', label: '9:16 Stories' },
  { value: '1:1', label: '1:1 Post' },
];

// Output size options with their aspect ratios
const outputSizeOptions = {
  '4:5': [{ value: '1080x1350', label: '1080x1350 Post' }],
  '9:16': [{ value: '1080x1920', label: '1080x1920 Stories' }],
  '1:1': [{ value: '1080x1080', label: '1080x1080 Post' }],
};

const Downscaler = () => {
  const startRead = useLatestRead();
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [aspectRatio, setAspectRatio] = useState('4:5');
  const [outputSize, setOutputSize] = useState('1080x1350');
  const [processing, setProcessing] = useState(false);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [hasMoreHistory, setHasMoreHistory] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showMoreClicked, setShowMoreClicked] = useState(false);
  const fileInputRef = useRef(null);
  const loadMoreRef = useRef(null);
  const previewUrlRef = useRef(null);
  const BATCH_SIZE = 12;
  const ITEMS_PER_ROW = 2;
  const INITIAL_ROWS = 2;
  const INITIAL_LIMIT = ITEMS_PER_ROW * INITIAL_ROWS;

  useEffect(() => {
    loadHistory();
  }, []);

  // Update output size when aspect ratio changes
  useEffect(() => {
    const sizes = outputSizeOptions[aspectRatio];
    if (sizes && sizes.length > 0) {
      setOutputSize(sizes[0].value);
    }
  }, [aspectRatio]);

  // Lazy loading observer
  useEffect(() => {
    const sentinel = loadMoreRef.current;
    if (!sentinel || !showMoreClicked || !hasMoreHistory || loadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            loadHistory({ append: true, limit: BATCH_SIZE, offset: history.length });
          }
        });
      },
      { rootMargin: '200px 0px', threshold: 0.1 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [history.length, showMoreClicked, hasMoreHistory, loadingMore]);

  useEffect(() => () => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
    }
  }, []);

  const setPreviewObjectUrl = (url) => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
    }
    previewUrlRef.current = url;
    setPreviewUrl(url);
  };

  const mergeHistory = (current, incoming) => {
    const merged = new Map();
    [...current, ...incoming].forEach((image) => {
      if (image?.id) {
        merged.set(image.id, image);
      }
    });
    return Array.from(merged.values()).sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  };

  const loadHistory = async ({ append = false, limit = INITIAL_LIMIT, offset = 0 } = {}) => {
    const read = startRead();
    if (!read.isCurrent()) return;
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const response = await downscalerAPI.getAll({ limit, offset, signal: read.signal });
      if (!read.isCurrent()) return;
      const sorted = [...(response.data || [])].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      setHistory((prev) => (append ? mergeHistory(prev, sorted) : sorted));
      setHasMoreHistory(sorted.length === limit);
    } catch (error) {
      if (!read.isCurrent()) return;
      console.error('Failed to load history:', error);
    } finally {
      if (read.isCurrent()) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'image/png') {
      setSelectedFile(file);
      const url = URL.createObjectURL(file);
      setPreviewObjectUrl(url);
    } else {
      alert('Please select a PNG file');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && file.type === 'image/png') {
      setSelectedFile(file);
      const url = URL.createObjectURL(file);
      setPreviewObjectUrl(url);
    } else {
      alert('Please drop a PNG file');
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleProcess = async () => {
    if (!selectedFile) return;

    setProcessing(true);
    try {
      const formData = new FormData();
      formData.append('image', selectedFile);
      formData.append('aspectRatio', aspectRatio);
      formData.append('outputSize', outputSize);

      await downscalerAPI.process(formData);

      // Clear selection and reload history
      setSelectedFile(null);
      setPreviewObjectUrl(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      loadHistory();
    } catch (error) {
      console.error('Failed to process image:', error);
      alert(error.response?.data?.error || 'Failed to process image');
    } finally {
      setProcessing(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;

    try {
      await downscalerAPI.delete(id);
      setSelectedImage(null);
      loadHistory();
    } catch (error) {
      console.error('Failed to delete image:', error);
    }
  };

  const handleDownload = (image) => {
    if (!image?.image_path) return;
    const link = document.createElement('a');
    link.href = buildAssetUrl(image.image_path);
    link.download = `downscaled-${image.id}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleShowMore = () => {
    setShowMoreClicked(true);
    if (!loadingMore && hasMoreHistory) {
      loadHistory({ append: true, limit: BATCH_SIZE, offset: history.length });
    }
  };

  const clearSelection = () => {
    setSelectedFile(null);
    setPreviewObjectUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="downscaler-container">
      <h1>Downscaler</h1>

      <div className="downscaler-content">
        <div className="downscaler-form-section">
          {/* Upload Zone */}
          <div
            className={`upload-zone ${previewUrl ? 'has-image' : ''}`}
            onClick={() => !previewUrl && fileInputRef.current?.click()}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
          >
            {previewUrl ? (
              <div className="preview-container">
                <OptimizedImage src={previewUrl} alt="Preview" className="preview-image" eager />
                <button
                  type="button"
                  className="btn-clear-preview"
                  onClick={(e) => {
                    e.stopPropagation();
                    clearSelection();
                  }}
                >
                  ×
                </button>
              </div>
            ) : (
              <div className="upload-placeholder">
                <div className="upload-icon">+</div>
                <p>Click or drag PNG image here</p>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
          </div>

          {/* Settings */}
          <div className="downscaler-settings">
            <div className="setting-group">
              <label>Aspect Ratio (Crop)</label>
              <div className="button-group">
                {aspectRatioOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={`btn-option ${aspectRatio === option.value ? 'active' : ''}`}
                    onClick={() => setAspectRatio(option.value)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="setting-group">
              <label>Output Size</label>
              <div className="button-group">
                {outputSizeOptions[aspectRatio]?.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={`btn-option ${outputSize === option.value ? 'active' : ''}`}
                    onClick={() => setOutputSize(option.value)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <button
              type="button"
              className="btn-process"
              onClick={handleProcess}
              disabled={!selectedFile || processing}
            >
              {processing ? 'Processing...' : 'Process Image'}
            </button>
          </div>
        </div>

        {/* History Section */}
        <div className="history-section">
          <h2>Processed Images</h2>
          {loading ? (
            <p>Loading...</p>
          ) : history.length === 0 ? (
            <p className="empty-message">
              No processed images yet. Upload a PNG to get started!
            </p>
          ) : (
            <VirtualGallery className="downscaler-grid">
              {history.map((image, index) => (
                <div key={image.id} className="downscaler-card">
                  <div
                    className="downscaler-image"
                    onClick={() => setSelectedImage(image)}
                  >
                    <OptimizedImage
                      src={getPreviewImagePath(image)}
                      alt={image.original_filename}
                      eager={index < INITIAL_LIMIT}
                    />
                  </div>
                  <div className="downscaler-info">
                    <p className="downscaler-filename">{image.original_filename}</p>
                    <div className="downscaler-meta">
                      <span>{image.aspect_ratio}</span>
                      <span>{image.output_size}</span>
                    </div>
                    {image.username && (
                      <p className="downscaler-user">
                        By: <span className="username">@{image.username}</span>
                      </p>
                    )}
                    <p className="downscaler-date">
                      {new Date(image.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              ))}
              {!loading && showMoreClicked && hasMoreHistory && (
                <div className="load-more-sentinel" ref={loadMoreRef} aria-hidden="true" />
              )}
            </VirtualGallery>
          )}
          {!loading && hasMoreHistory && (
            <button type="button" className="btn-show-more" onClick={handleShowMore} disabled={loadingMore}>
              {loadingMore ? 'Loading...' : 'Show More'}
            </button>
          )}
        </div>
      </div>

      {/* Modal */}
      {selectedImage && (
        <div className="modal-overlay" onClick={() => setSelectedImage(null)}>
          <button className="modal-close" onClick={() => setSelectedImage(null)}>
            ×
          </button>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-image-section">
              <OptimizedImage
                src={selectedImage.image_path}
                alt={selectedImage.original_filename}
                className="modal-image"
                eager
              />
            </div>
            <div className="modal-details-section">
              <button
                onClick={() => handleDownload(selectedImage)}
                className="btn-modal-download"
              >
                Download
                <span className="btn-icon-text">⬇</span>
              </button>

              <div className="modal-detail-group">
                <div className="modal-detail-label">Original Filename</div>
                <div className="modal-detail-value">{selectedImage.original_filename}</div>
              </div>

              <div className="modal-detail-group">
                <div className="modal-detail-label">Settings</div>
                <div className="modal-meta-tags">
                  <span>{selectedImage.aspect_ratio}</span>
                  <span>{selectedImage.output_size}</span>
                </div>
              </div>

              {selectedImage.username && (
                <div className="modal-detail-group">
                  <div className="modal-detail-label">Processed By</div>
                  <div className="modal-detail-value">
                    <span className="modal-username">@{selectedImage.username}</span>
                  </div>
                </div>
              )}

              <div className="modal-detail-group">
                <div className="modal-detail-label">Created</div>
                <div className="modal-detail-value">
                  {new Date(selectedImage.created_at).toLocaleString()}
                </div>
              </div>

              <button
                onClick={() => handleDelete(selectedImage.id)}
                className="btn-modal-delete"
              >
                Delete
                <span className="btn-icon-text">🗑</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Downscaler;
